import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';
import { Input } from '@/shared/components/ui/Input';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Button } from '@/shared/components/ui/Button';
import { formatPhoneNumber } from '@/shared/utils';
import type { HistoryImportChatSummary, HistoryImportRun } from '@troia-v3/types';

import { useImportChats, useIngestImport } from '../../hooks/useHistoryImport';

/** Server pagina em 50/página (`historyImportApi.listChats`) — usado só para calcular `totalPages`. */
const PAGE_LIMIT = 50;

export interface ChatSelectionStepProps {
  runId: string;
  run: HistoryImportRun;
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

interface ChatDisplay {
  primary: string;
  secondary?: string;
}

/**
 * Deriva o par (linha primária, linha secundária) exibido por chat na lista
 * de seleção — UX fix B (task-20-report.md).
 *
 * Prioridade da linha primária: nome capturado/enriquecido por contato
 * (`chat.name` — o backend já preenche esse campo com o nome do nosso
 * contato QUANDO vazio, ver `HistoryImportsService.enrichChatNamesFromContacts`)
 * > `pushName` > telefone formatado (só pra `@s.whatsapp.net`) > fallback
 * textual — NUNCA o JID cru como linha primária (um `@lid` sem resolução é
 * ilegível: `250267979219110@lid`).
 *
 * Linha secundária: telefone formatado quando o chat é um número resolvido
 * (`@s.whatsapp.net`) — omitida quando a primária já É o telefone (evita
 * repetir a mesma informação 2x). Para `@lid` não resolvido, mostra "ID
 * vinculado" no lugar do telefone (nunca o LID gigante).
 */
function getChatDisplay(chat: HistoryImportChatSummary): ChatDisplay {
  const isWhatsAppNumber = chat.chatId.endsWith('@s.whatsapp.net');
  const isUnresolvedLid = chat.chatId.endsWith('@lid');

  const phoneFormatted = isWhatsAppNumber ? formatPhoneNumber(chat.chatId.split('@')[0]) : undefined;

  if (chat.name) {
    return { primary: chat.name, secondary: phoneFormatted ?? (isUnresolvedLid ? 'ID vinculado' : undefined) };
  }
  if (chat.pushName) {
    return { primary: chat.pushName, secondary: phoneFormatted ?? (isUnresolvedLid ? 'ID vinculado' : undefined) };
  }
  if (phoneFormatted) {
    // Sem nome nem pushName — a própria linha primária já é o telefone,
    // então não repete na secundária.
    return { primary: phoneFormatted };
  }
  if (isUnresolvedLid) {
    return { primary: 'Contato sem nome', secondary: 'ID vinculado' };
  }
  return { primary: chat.chatId };
}

/**
 * Passo 3 do wizard — lista paginada + busca dos chats capturados
 * (`ready_for_selection`), seleção via checkboxes/select-all, período fino
 * (2 date inputs) e disparo da ingestão.
 *
 * O intervalo "capturado" pela sessão NÃO é `run.periodStart`/`run.periodEnd`
 * (esses campos só são preenchidos DEPOIS do ingest, ver
 * `HistoryImportsService.ingest` em `history-imports/service.ts:369-370`) —
 * é derivado da mesma fórmula que o backend usa para validar o corte fino:
 * `[run.createdAt - run.periodDays dias, agora]`.
 */
export const ChatSelectionStep: React.FC<ChatSelectionStepProps> = ({ runId, run }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());

  // Debounce de 400ms na busca (convenção do projeto — AudiencesPage.tsx).
  // O reset de página acompanha o valor DEBOUNCED (não cada tecla).
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useImportChats(runId, run.status, page, debouncedSearch || undefined);
  const ingestMutation = useIngestImport();

  const { capturedStartISO, capturedEndISO, minDate, maxDate } = useMemo(() => {
    const start = new Date(new Date(run.createdAt).getTime() - run.periodDays * 24 * 60 * 60 * 1000);
    const end = new Date();
    return {
      capturedStartISO: start.toISOString(),
      capturedEndISO: end.toISOString(),
      minDate: toDateInputValue(start),
      maxDate: toDateInputValue(end),
    };
    // Deliberadamente fixado na primeira renderização deste passo (a janela
    // capturada não deve "andar" enquanto o usuário ajusta o período fino).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [startDate, setStartDate] = useState(() => capturedStartISO.slice(0, 10));
  const [endDate, setEndDate] = useState(() => capturedEndISO.slice(0, 10));

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  const allOnPageSelected = items.length > 0 && items.every((chat) => selectedChatIds.has(chat.chatId));
  const someOnPageSelected = items.some((chat) => selectedChatIds.has(chat.chatId));

  const toggleChat = (chatId: string, checked: boolean): void => {
    setSelectedChatIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(chatId);
      else next.delete(chatId);
      return next;
    });
  };

  const toggleSelectAllOnPage = (checked: boolean): void => {
    setSelectedChatIds((prev) => {
      const next = new Set(prev);
      items.forEach((chat) => {
        if (checked) next.add(chat.chatId);
        else next.delete(chat.chatId);
      });
      return next;
    });
  };

  // Se o usuário não tocou no campo (valor == dia capturado), reenvia a ISO
  // exata do backend — evita que o arredondamento pra início/fim do dia
  // extrapole o intervalo capturado (que tem timestamp, não só data) e
  // dispare o 422 "Período fora do intervalo capturado".
  const resolvePeriodStart = (): string =>
    startDate === capturedStartISO.slice(0, 10) ? capturedStartISO : `${startDate}T00:00:00.000Z`;
  const resolvePeriodEnd = (): string =>
    endDate === capturedEndISO.slice(0, 10) ? capturedEndISO : `${endDate}T23:59:59.999Z`;

  const handleIngest = (): void => {
    if (selectedChatIds.size === 0) return;
    ingestMutation.mutate({
      runId,
      chatIds: Array.from(selectedChatIds),
      periodStart: resolvePeriodStart(),
      periodEnd: resolvePeriodEnd(),
    });
  };

  return (
    <div className="space-y-4" data-testid="chat-selection-step">
      <Input
        icon="search"
        placeholder="Buscar por nome ou telefone"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="chat-search-input"
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
            Início do período
          </p>
          <Input
            type="date"
            value={startDate}
            min={minDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="import-period-start-input"
          />
        </div>
        <div>
          <p className="text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
            Fim do período
          </p>
          <Input
            type="date"
            value={endDate}
            min={startDate}
            max={maxDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="import-period-end-input"
          />
        </div>
      </div>

      <div className="flex items-center justify-between px-0.5">
        <Checkbox
          label={`Selecionar todos da página (${items.length})`}
          checked={allOnPageSelected}
          indeterminate={!allOnPageSelected && someOnPageSelected}
          onChange={toggleSelectAllOnPage}
          disabled={items.length === 0}
          data-testid="select-all-chats"
        />
        <p className="text-xs" style={{ color: '#6b7280' }} data-testid="chat-selection-count">
          {selectedChatIds.size} selecionada(s) de {total}
        </p>
      </div>

      <div
        className="border rounded-lg max-h-80 overflow-y-auto"
        style={{ borderColor: '#e5e7eb' }}
        data-testid="chat-selection-list"
      >
        {isLoading && (
          <p className="text-sm p-4 text-center" style={{ color: '#6b7280' }} data-testid="chat-selection-loading">
            Carregando conversas...
          </p>
        )}
        {!isLoading && items.length === 0 && (
          <p className="text-sm p-4 text-center" style={{ color: '#6b7280' }} data-testid="chat-selection-empty">
            Nenhuma conversa encontrada.
          </p>
        )}
        {!isLoading &&
          items.map((chat, idx) => {
            const display = getChatDisplay(chat);
            return (
              <div
                key={chat.chatId}
                className={clsx('flex items-center gap-3 p-3', idx > 0 && 'border-t')}
                style={idx > 0 ? { borderColor: '#e5e7eb' } : undefined}
                data-testid={`chat-selection-row-${chat.chatId}`}
              >
                <Checkbox
                  checked={selectedChatIds.has(chat.chatId)}
                  onChange={(checked) => toggleChat(chat.chatId, checked)}
                  data-testid={`chat-checkbox-${chat.chatId}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                    {display.primary}
                  </p>
                  {display.secondary && (
                    <p className="text-xs truncate" style={{ color: '#6b7280' }}>
                      {display.secondary}
                    </p>
                  )}
                  <p className="text-xs" style={{ color: '#6b7280' }}>
                    {formatDateShort(chat.firstMsgAt)} – {formatDateShort(chat.lastMsgAt)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs" style={{ color: '#6b7280' }}>
                    {chat.msgCount} msgs
                  </p>
                  {chat.mediaCount > 0 && (
                    <p className="text-xs flex items-center gap-1 justify-end" style={{ color: '#6b7280' }}>
                      <Icon name="image" className="w-3 h-3" /> {chat.mediaCount}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            data-testid="chat-selection-prev-page"
          >
            Anterior
          </Button>
          <p className="text-xs" style={{ color: '#6b7280' }}>
            Página {page} de {totalPages}
          </p>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            data-testid="chat-selection-next-page"
          >
            Próxima
          </Button>
        </div>
      )}

      <Button
        variant="primary"
        fullWidth
        loading={ingestMutation.isPending}
        disabled={selectedChatIds.size === 0}
        onClick={handleIngest}
        data-testid="ingest-button"
      >
        Importar selecionadas ({selectedChatIds.size})
      </Button>
    </div>
  );
};

ChatSelectionStep.displayName = 'ChatSelectionStep';
